import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Rocket, BadgeCheck, Clock, Pencil, Save, X, ShieldCheck, AlertTriangle, AlertCircle, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ChallengeDayRow from "@/components/creator/ChallengeDayRow";
import ChallengeDayEditorPanel from "@/components/creator/ChallengeDayEditorPanel";

const SEVERITY_META = {
  high: { icon: AlertCircle, cls: "text-red-300 bg-red-400/10" },
  medium: { icon: AlertTriangle, cls: "text-amber-300 bg-amber-400/10" },
  low: { icon: Info, cls: "text-slate-300 bg-slate-400/10" },
};

export default function CreatorChallengeBuilderPage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [days, setDays] = useState([]);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingDay, setEditingDay] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({ title: "", category: "", description: "" });
  const [savingMeta, setSavingMeta] = useState(false);
  const [qualityIssues, setQualityIssues] = useState(null);
  const [checkingQuality, setCheckingQuality] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/custom-challenges/${challengeId}/builder`);
      setChallenge(data.challenge);
      setDays(data.days);
      setReviewedCount(data.reviewed_count);
      setMetaForm({ title: data.challenge.title, category: data.challenge.category, description: data.challenge.description });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load the builder.");
    } finally {
      setLoading(false);
    }
  }, [challengeId]);

  useEffect(() => { load(); }, [load]);

  const locked = !!challenge?.is_published;

  const handleReorder = async (newOrder) => {
    setDays(newOrder);
    try {
      await api.post(`/custom-challenges/${challengeId}/days/reorder`, { day_ids: newOrder.map((d) => d.id) });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reorder days");
      load();
    }
  };

  const handleInsertAfter = async (afterDay) => {
    try {
      await api.post(`/custom-challenges/${challengeId}/days/insert`, { after_day: afterDay });
      toast.success("New day inserted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to insert a day");
    }
  };

  const handleDelete = async (day) => {
    try {
      await api.delete(`/custom-challenges/${challengeId}/day/${day}`);
      toast.success(`Day ${day} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete this day");
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await api.post(`/custom-challenges/${challengeId}/publish`);
      toast.success("Published! Anyone can now discover this challenge in the Marketplace.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't publish this challenge.");
    } finally {
      setPublishing(false);
    }
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const { data } = await api.put(`/custom-challenges/${challengeId}`, metaForm);
      setChallenge((prev) => ({ ...prev, ...data }));
      setEditingMeta(false);
      toast.success("Challenge details updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update details");
    } finally {
      setSavingMeta(false);
    }
  };

  const runQualityCheck = async () => {
    setCheckingQuality(true);
    setQualityIssues(null);
    try {
      const { data } = await api.post(`/custom-challenges/${challengeId}/quality-check`);
      setQualityIssues(data.issues);
      if (data.issues.length === 0) toast.success("No issues found — this roadmap looks ready to publish!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Quality check failed");
    } finally {
      setCheckingQuality(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="challenge-builder-loading">
        <Loader2 className="w-6 h-6 text-[#5C9DFF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="challenge-builder-error">
        <p className="text-slate-300">{error}</p>
        <Link to="/creator/studio" className="text-indigo-400 hover:text-indigo-300 text-sm">Back to Creator Studio</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="challenge-builder-page">
      <button onClick={() => navigate("/creator/studio")} data-testid="challenge-builder-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Creator Studio
      </button>

      <div className="glass-panel rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">{challenge.category}</span>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-white/5 text-slate-400">{challenge.creation_mode === "manual" ? "Manual Creator" : "AI Creator"}</span>
              {locked ? (
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-300"><BadgeCheck className="w-3 h-3" /> Published (read-only)</span>
              ) : (
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-400/10 text-slate-400"><Clock className="w-3 h-3" /> Draft</span>
              )}
            </div>

            {!editingMeta ? (
              <>
                <div className="flex items-center gap-2 mt-2">
                  <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white" data-testid="challenge-builder-title">{challenge.title}</h1>
                  {!locked && <button onClick={() => setEditingMeta(true)} data-testid="challenge-builder-edit-meta-btn" className="text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>}
                </div>
                <p className="text-sm text-slate-400 mt-2 max-w-xl">{challenge.description}</p>
              </>
            ) : (
              <div className="mt-3 space-y-2 max-w-lg">
                <input value={metaForm.title} onChange={(e) => setMetaForm({ ...metaForm, title: e.target.value })} data-testid="challenge-builder-title-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-indigo-400/50" />
                <input value={metaForm.category} onChange={(e) => setMetaForm({ ...metaForm, category: e.target.value })} data-testid="challenge-builder-category-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-indigo-400/50" />
                <textarea value={metaForm.description} onChange={(e) => setMetaForm({ ...metaForm, description: e.target.value })} rows={2} data-testid="challenge-builder-description-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-indigo-400/50" />
                <div className="flex items-center gap-2">
                  <button onClick={saveMeta} disabled={savingMeta} data-testid="challenge-builder-save-meta-btn" className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-3 py-1.5 rounded-lg">
                    {savingMeta ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                  </button>
                  <button onClick={() => setEditingMeta(false)} data-testid="challenge-builder-cancel-meta-btn" className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white px-3 py-1.5 rounded-lg">
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          {!locked && (
            <div className="shrink-0 flex items-center gap-2">
              <button onClick={runQualityCheck} disabled={checkingQuality} data-testid="challenge-builder-quality-check-btn"
                className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-60 text-slate-200 font-semibold px-4 py-2.5 rounded-xl transition-all">
                {checkingQuality ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} AI Quality Check
              </button>
              <button onClick={handlePublish} disabled={publishing} data-testid="challenge-builder-publish-btn"
                className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-xl transition-all">
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Publish
              </button>
            </div>
          )}
        </div>

        {qualityIssues !== null && (
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="quality-check-results-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                {qualityIssues.length === 0 ? "No issues found" : `${qualityIssues.length} issue${qualityIssues.length > 1 ? "s" : ""} found`}
              </p>
              <button onClick={() => setQualityIssues(null)} data-testid="quality-check-dismiss-btn" className="text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
            </div>
            {qualityIssues.length > 0 && (
              <div className="space-y-2">
                {qualityIssues.map((issue, i) => {
                  const meta = SEVERITY_META[issue.severity] || SEVERITY_META.low;
                  const Icon = meta.icon;
                  const targetDay = days.find((d) => d.day === issue.day);
                  return (
                    <div key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`quality-issue-row-${i}`}>
                      <span className="flex items-start gap-2">
                        <span className={`shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${meta.cls}`}><Icon className="w-3 h-3 inline mr-1" />Day {issue.day}</span>
                        <span className="text-slate-300">{issue.message}</span>
                      </span>
                      {targetDay && (
                        <button onClick={() => { setEditingDay(targetDay); setQualityIssues(null); }} data-testid={`quality-issue-fix-btn-${i}`} className="shrink-0 text-xs font-semibold text-indigo-300 hover:text-indigo-200">Fix</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="mt-5">
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span data-testid="challenge-builder-reviewed-label">Day {reviewedCount}/{challenge.total_days} reviewed</span>
            <span className="font-mono">{Math.round((reviewedCount / challenge.total_days) * 100)}%</span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#5C9DFF] to-[#B58BFF]" style={{ width: `${Math.max((reviewedCount / challenge.total_days) * 100, 3)}%` }} />
          </div>
        </div>
      </div>

      <Reorder.Group as="div" axis="y" values={days} onReorder={locked ? () => {} : handleReorder} className="space-y-2.5" data-testid="challenge-builder-day-list">
        {days.map((d) => (
          <ChallengeDayRow key={d.id} day={d} locked={locked} onEdit={setEditingDay} onDelete={handleDelete} onInsertAfter={handleInsertAfter} />
        ))}
      </Reorder.Group>

      {editingDay && (
        <ChallengeDayEditorPanel
          challengeId={challengeId}
          day={editingDay}
          readOnly={locked}
          onClose={() => setEditingDay(null)}
          onSaved={(updated) => {
            setDays((prev) => prev.map((d) => (d.day === editingDay.day ? { ...d, ...updated } : d)));
            setEditingDay(null);
            load();
          }}
        />
      )}
    </div>
  );
}
